import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/landing-redesign.css';
import '../styles/events-redesign.css';
import eventsData from '../data/events.json';

interface Event {
  id: string;
  name: string;
  category: string;
  description: string;
}

const FieldEventsRedesign: React.FC = () => {
  const [fieldEvents, setFieldEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNavScrolled, setIsNavScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll handler for nav
    const handleScroll = () => {
      setIsNavScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Simulate loading and filter for field events
    const timer = setTimeout(() => {
      const filteredEvents = eventsData.filter(event => event.category === 'Field');
      setFieldEvents(filteredEvents);
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Field event icon SVG (jumping/throwing)
  const FieldIcon = () => (
    <svg className="events-type-icon" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14.12,10H19V8.2H15.38L13.38,4.87C13.08,4.37 12.54,4.03 11.92,4.03C11.74,4.03 11.58,4.06 11.42,4.11L6,5.8V11H7.8V7.33L9.91,6.67L6,22H7.8L10.67,13.89L13,17V22H14.8V15.59L12.31,11.05L13.04,8.18M14,3.8C15,3.8 15.8,3 15.8,2C15.8,1 15,0.2 14,0.2C13,0.2 12.2,1 12.2,2C12.2,3 13,3.8 14,3.8Z"/>
    </svg>
  );

  return (
    <div className="events-redesign landing-redesign field-page">
      {/* Animated Background Elements */}
      <div className="track-pattern-bg" />
      <div className="particle-field">
        {[...Array(10)].map((_, i) => (
          <div key={i} className="particle" style={{ background: i % 2 === 0 ? '#009739' : '#FFD700' }} />
        ))}
      </div>

      {/* Track Lane Background */}
      <div className="events-track-lines">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="track-lane" style={{ 
            background: `linear-gradient(90deg, 
              transparent 0%,
              rgba(0, 151, 57, 0.03) 20%,
              rgba(0, 151, 57, 0.05) 50%,
              rgba(0, 151, 57, 0.03) 80%,
              transparent 100%)`
          }} />
        ))}
        {[...Array(5)].map((_, i) => (
          <div key={i} className="speed-line" style={{
            background: `linear-gradient(90deg, 
              transparent,
              rgba(0, 151, 57, 0.6),
              rgba(0, 212, 255, 0.8),
              rgba(0, 151, 57, 0.6),
              transparent)`
          }} />
        ))}
      </div>

      {/* Navigation */}
      <nav className={`athletic-nav ${isNavScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="nav-brand">
            <img 
              src="/assets/moe-logo.jpg" 
              alt="MOE Athletics" 
              className="nav-logo-img"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="45" fill="%23009739"/%3E%3Ctext x="50" y="60" text-anchor="middle" fill="white" font-size="24" font-weight="bold"%3EMOE%3C/text%3E%3C/svg%3E';
              }}
            />
            <div className="nav-brand-text">
              <h1 className="nav-title">Athletics</h1>
              <span className="nav-subtitle">Regional Inter Secondary Schools</span>
            </div>
          </Link>

          <div className="nav-menu">
            <Link to="/" className="nav-menu-link">Home</Link>
            <Link to="/track-events" className="nav-menu-link">Track Events</Link>
            <Link to="/field-events" className="nav-menu-link active">Field Events</Link>
          </div>

          <div className="nav-actions">
            <button 
              className="btn-athletic btn-secondary-athletic"
              onClick={() => navigate('/')}
            >
              Login
            </button>
            <button 
              className="btn-athletic btn-primary-athletic"
              onClick={() => navigate('/')}
            >
              Register
            </button>
          </div>

          <button className="mobile-menu-toggle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="events-hero">
        <div 
          className="events-hero-bg" 
          style={{ backgroundImage: 'url(/assets/landingwallpaper.jpg)' }}
        />
        
        {/* Diagonal Stripes */}
        <div className="events-hero-stripes">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="hero-stripe" style={{
              background: `linear-gradient(90deg, 
                transparent 0%, 
                rgba(0, 151, 57, 0.8) 50%, 
                transparent 100%)`
            }} />
          ))}
        </div>
        
        <div className="events-hero-overlay" style={{
          background: `
            radial-gradient(ellipse at 30% 20%, rgba(0, 151, 57, 0.4) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 80%, rgba(0, 212, 255, 0.2) 0%, transparent 50%),
            linear-gradient(180deg, 
              rgba(10, 14, 23, 0.3) 0%, 
              rgba(10, 14, 23, 0.7) 50%,
              #0A0E17 100%)`
        }} />

        <div className="events-hero-content">
          <div className="events-type-badge field-type">
            <FieldIcon />
            <span className="events-type-text">Power & Precision</span>
          </div>

          <h1 className="events-page-title">
            <span className="title-highlight">Field</span> Events
          </h1>

          <p className="events-page-subtitle">
            Throwing, jumping, and technical athletic competitions
          </p>

          <div className="events-stats-row">
            <div className="event-stat-item">
              <div className="event-stat-number">{fieldEvents.length}</div>
              <div className="event-stat-label">Events</div>
            </div>
            <div className="event-stat-item">
              <div className="event-stat-number">4</div>
              <div className="event-stat-label">Age Groups</div>
            </div>
            <div className="event-stat-item">
              <div className="event-stat-number">2</div>
              <div className="event-stat-label">Genders</div>
            </div>
          </div>
        </div>
      </section>

      {/* Events Grid Section */}
      <section className="events-grid-section">
        <div className="events-grid-header">
          <h2 className="events-section-title">All Field Events</h2>
          <div className="events-count-badge" style={{
            background: 'rgba(0, 151, 57, 0.1)',
            borderColor: 'rgba(0, 151, 57, 0.3)',
            color: '#009739'
          }}>
            {fieldEvents.length} Events Available
          </div>
        </div>

        {isLoading ? (
          <div className="events-loading-athletic">
            <div className="loading-track">
              <div className="loading-runner" style={{
                background: 'linear-gradient(90deg, #009739, #00D4FF)'
              }} />
            </div>
            <span className="loading-text">Loading Field Events...</span>
          </div>
        ) : fieldEvents.length === 0 ? (
          <div className="events-empty-athletic">
            <div className="events-empty-icon">🏋️</div>
            <h3 className="events-empty-title">No Field Events Found</h3>
            <p className="events-empty-text">Check back soon for upcoming field events.</p>
          </div>
        ) : (
          <div className="events-grid-athletic">
            {fieldEvents.map((event, index) => (
              <article 
                key={event.id} 
                className="event-card-athletic"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  animation: 'fadeInUp 0.6s ease forwards',
                  opacity: 0
                }}
              >
                <div className="event-card-header">
                  <h3 className="event-card-title">{event.name}</h3>
                  <span className="event-category-badge" style={{
                    background: 'linear-gradient(135deg, #009739, #00D4FF)',
                    boxShadow: '0 4px 15px rgba(0, 151, 57, 0.3)'
                  }}>{event.category}</span>
                </div>

                <p className="event-card-description">{event.description}</p>

                <div className="event-tags-container">
                  {/* Gender Tags */}
                  <div className="event-tags-group">
                    <span className="event-tags-label">Gender Categories</span>
                    <div className="event-tags-list">
                      <span className="event-tag male">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
                          <path d="M9.5,11c1.93,0,3.5,1.57,3.5,3.5S11.43,18,9.5,18S6,16.43,6,14.5S7.57,11,9.5,11M9.5,9C6.46,9,4,11.46,4,14.5S6.46,20,9.5,20s5.5-2.46,5.5-5.5c0-1.16-0.36-2.23-0.97-3.12L18,7.42V10h2V4h-6v2h2.58l-3.97,3.97C11.73,9.36,10.66,9,9.5,9z"/>
                        </svg>
                        Male
                      </span>
                      <span className="event-tag female">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
                          <path d="M12,4A6,6 0 0,1 18,10C18,12.97 15.84,15.44 13,15.92V18H15V20H13V22H11V20H9V18H11V15.92C8.16,15.44 6,12.97 6,10A6,6 0 0,1 12,4M12,6A4,4 0 0,0 8,10A4,4 0 0,0 12,14A4,4 0 0,0 16,10A4,4 0 0,0 12,6Z"/>
                        </svg>
                        Female
                      </span>
                    </div>
                  </div>

                  {/* Age Tags */}
                  <div className="event-tags-group">
                    <span className="event-tags-label">Age Categories</span>
                    <div className="event-tags-list">
                      <span className="event-tag age">Grade 7</span>
                      <span className="event-tag age">Under 15</span>
                      <span className="event-tag age">Under 17</span>
                      <span className="event-tag age">Under 20</span>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Add keyframe animation */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .field-page .event-card-athletic::before {
          background: linear-gradient(90deg, 
            #009739,
            #00D4FF,
            #FFD700) !important;
        }
      `}</style>
    </div>
  );
};

export default FieldEventsRedesign;
